import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { breadcrumbJsonLd } from "../components/Breadcrumbs";
import { SERVICES, ZONES } from "../data/services";
import { PHONE_DISPLAY, PHONE_TEL } from "../components/Layout";
import { siteUrl } from "../lib/siteUrl";
import { ReviewsSection, VoiceFaqSection, MainFaqSection } from "../components/home/HomeSections";
import ResponsiveImage from "../components/ResponsiveImage";
import TableOfContents from "../components/TableOfContents";

const TOC_ITEMS = [
    ["#services-h2", "Nos services de réparation"],
    ["#why-h2", "Pourquoi choisir Electrocase"],
    ["#zones-h2", "Zones d'intervention"],
    ["#reviews-h2", "Avis clients"],
    ["#voice-faq-h2", "L'essentiel en 30 secondes"],
    ["#faq-h2", "Questions fréquentes"],
];

const SERVICE_TITLES = {
    "Lave-linge": "Réparateur lave-linge",
    "Lave-vaisselle": "Réparateur lave-vaisselle",
    "Frigo": "Réparateur frigo & congélateur",
    "Four": "Réparation four & micro-ondes",
    "Sèche-linge": "Réparation sèche-linge",
};

const serviceTitle = (nav) => SERVICE_TITLES[nav] || `Réparation ${nav.toLowerCase()}`;

const SERVICE_IMG = {
    "reparateur-lave-linge-paris": "washing-machine",
    "reparateur-lave-vaisselle-paris": "kitchen-clean",
    "reparateur-frigo-paris": "fridge-modern",
    "reparation-four-paris": "oven-knobs",
    "reparation-seche-linge-paris": "hero-laundry-pair",
};

const STATS = [
    { v: "1000+", l: "Clients satisfaits" },
    { v: "3500+", l: "Appareils réparés" },
    { v: "95 %", l: "Interventions sous 48 h" },
    { v: "4,9/5", l: "Note moyenne clients" },
];

const WHY = [
    { i: "fa-solid fa-bolt", t: "Intervention sous 24–48 h", d: "Diagnostic à domicile rapide à Paris et en Île-de-France, créneaux le jour même selon planning." },
    { i: "fa-solid fa-shield-halved", t: "Réparation garantie 18 mois", d: "Pièces d'origine ou équivalentes premium. Garantie pièces & main-d'œuvre." },
    { i: "fa-solid fa-euro-sign", t: "Tarifs transparents", d: "Devis ferme avant intervention. Pas de surprise, pas de frais cachés." },
    { i: "fa-solid fa-leaf", t: "Démarche écoresponsable", d: "Réparer plutôt que jeter : moins de déchets, moins de carbone, plus d'économies." },
];

const FAQ = [
    { q: "Quel est le tarif d'un dépannage électroménager à domicile ?", a: "Le devis est **gratuit par téléphone**. La main-d'œuvre est forfaitaire et toujours validée avant intervention. Voir la page [Tarifs](/tarifs) ou notre [barème détaillé par panne](/blog/prix-reparation-electromenager-2026). Appelez-nous au " + PHONE_DISPLAY + "." },
    { q: "Sous quel délai intervenez-vous ?", a: "Dans la majorité des cas, sous **24 à 48 h** à Paris et Hauts-de-Seine. Pour les urgences (fuite, frigo HS), nous proposons un créneau le jour même selon planning. Voir la liste des [Zones d'intervention](/zones-intervention)." },
    { q: "Réparez-vous toutes les marques d'électroménager ?", a: "Oui : **Bosch, Samsung, LG, Whirlpool, Miele, Siemens, Electrolux, Beko, Haier, Candy, Indesit, AEG, Smeg, Liebherr, De Dietrich, Brandt, Vedette, Thomson** et bien d'autres. Voir notre classement complet : [Marques les plus fiables 2026](/blog/marques-electromenager-fiables-2026)." },
    { q: "Quelles sont vos zones d'intervention ?", a: "Paris (tous arrondissements), Hauts-de-Seine (92), Yvelines (78), Val-de-Marne (94) — plus de 35 villes desservies. Voir la liste complète sur la page [Zones d'intervention](/zones-intervention)." },
    { q: "La réparation est-elle garantie ?", a: "Oui, **garantie 18 mois** pièces et main-d'œuvre. Si la même panne réapparaît dans le délai, nous revenons gratuitement. Détail des conditions sur la [FAQ complète](/faq#garantie)." },
    { q: "Faut-il mieux réparer ou remplacer mon appareil ?", a: "Pour un appareil de moins de **8–10 ans**, la réparation est presque toujours plus économique et écologique. Nous donnons un avis transparent ; si le coût dépasse 50 % du prix neuf, nous vous le disons. Notre guide complet : [Réparer ou remplacer en 2026](/blog/reparer-ou-remplacer-electromenager)." },
];

/**
 * VOICE FAQ — réponses courtes optimisées pour assistants vocaux (Google Assistant, Alexa, Siri).
 * Critères : question < 10 mots, réponse 1-2 phrases conversationnelles, sans listes.
 * Marquées via class "speakable" + data-speakable et référencées par CSS dans le schéma SpeakableSpecification.
 */
const VOICE_FAQ = [
    { q: "Qui répare l'électroménager à Paris ?", a: "Electrocase intervient à domicile dans tout Paris et l'Île-de-France pour réparer lave-linge, frigo, lave-vaisselle, four et sèche-linge sous 24 à 48 heures. Devis gratuit au " + PHONE_DISPLAY + "." },
    { q: "Combien coûte une réparation à domicile ?", a: "Le devis est gratuit par téléphone et le tarif final est toujours annoncé avant intervention. Aucun frais caché, pas de surprise sur la facture." },
    { q: "Sous quel délai pouvez-vous venir ?", a: "Nous intervenons sous 24 à 48 heures à Paris et en proche couronne. Pour les urgences comme une fuite ou un frigo en panne, intervention le jour même selon planning." },
    { q: "Vos réparations sont-elles garanties ?", a: "Oui, toutes nos réparations sont garanties 18 mois, pièces et main-d'œuvre. Si la même panne réapparaît dans ce délai, nous revenons gratuitement." },
];

/* Markdown helpers used only for JSON-LD `stripMarkdown` (the inline JSX renderer
 * was moved to HomeSections.jsx along with the FAQ section). */
function stripMarkdown(str) {
    return str.replace(/\*\*([^*]+)\*\*/g, "$1").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
}

export default function HomePage() {
    const homeJsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "WebSite",
                "@id": siteUrl("/#website"),
                "url": siteUrl("/"),
                "name": "Electrocase",
                "inLanguage": "fr-FR",
                "publisher": { "@id": siteUrl("/#business") },
            },
            {
                "@type": "WebPage",
                "@id": siteUrl("/#webpage"),
                "url": siteUrl("/"),
                "name": "Réparateur Électroménager Paris & Île-de-France | Electrocase",
                "isPartOf": { "@id": siteUrl("/#website") },
                "inLanguage": "fr-FR",
                "speakable": {
                    "@type": "SpeakableSpecification",
                    "cssSelector": [".speakable-headline", ".speakable-voice-faq"],
                },
            },
            breadcrumbJsonLd([{ name: "Accueil", to: "/" }]),
            {
                "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
                "@id": siteUrl("/#business"),
                "name": "Electrocase",
                "alternateName": "Electrocase Réparation Électroménager",
                "url": siteUrl("/"),
                "logo": siteUrl("/og.jpg"),
                "image": siteUrl("/og.jpg"),
                "description": "Réparation et dépannage d'électroménager à domicile à Paris, Hauts-de-Seine, Yvelines et Val-de-Marne — toutes marques. Intervention sous 24 à 48 h, garantie 18 mois.",
                "telephone": PHONE_DISPLAY,
                "email": "postmaster@electrocase.fr",
                "priceRange": "€€",
                "currenciesAccepted": "EUR",
                "paymentAccepted": "Cash, Credit Card, Bank Transfer",
                "address": {
                    "@type": "PostalAddress",
                    "addressLocality": "Paris",
                    "addressRegion": "Île-de-France",
                    "postalCode": "75000",
                    "addressCountry": "FR",
                },
                "areaServed": [
                    { "@type": "AdministrativeArea", "name": "Île-de-France" },
                    { "@type": "City", "name": "Paris" },
                    { "@type": "AdministrativeArea", "name": "Hauts-de-Seine" },
                    { "@type": "AdministrativeArea", "name": "Yvelines" },
                    { "@type": "AdministrativeArea", "name": "Val-de-Marne" },
                ],
                "openingHoursSpecification": [
                    { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], "opens": "08:00", "closes": "20:00" },
                ],
            },
            {
                "@type": "FAQPage",
                "mainEntity": [...VOICE_FAQ, ...FAQ].map((f) => ({
                    "@type": "Question",
                    "name": f.q,
                    "acceptedAnswer": { "@type": "Answer", "text": stripMarkdown(f.a) },
                })),
            },
        ],
    };

    return (
        <>
            <Seo
                title="Réparateur électroménager Paris & IDF – 24 h | Electrocase"
                description="Réparateur électroménager à domicile à Paris & IDF (92, 78, 94). Lave-linge, frigo, lave-vaisselle, four. Sous 24-48 h. ☎ 01 84 80 46 86."
                keywords="réparateur lave-linge, réparateur lave-vaisselle, réparateur frigo, réparation électroménager Paris, dépannage électroménager domicile"
                jsonLd={homeJsonLd}
                canonical={siteUrl("/")}
                preloadHero="hero-laundry"
            />

            {/* HERO */}
            <section className="relative overflow-hidden grain">
                <div className="absolute -top-32 -right-32 w-[520px] h-[520px] bg-[var(--amber)]/20 rounded-full blur-3xl pointer-events-none" aria-hidden />
                <div className="absolute -bottom-32 -left-20 w-[420px] h-[420px] bg-[var(--navy)]/10 rounded-full blur-3xl pointer-events-none" aria-hidden />

                <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-14 lg:pt-20 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-12 items-center relative">
                    <div className="lg:col-span-7">
                        <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--navy)]/80 bg-white/70 border border-[var(--line)] rounded-full px-3 py-1.5 rise" data-testid="hero-eyebrow">
                            <i className="fa-solid fa-location-dot text-[var(--amber)]" />
                            Paris · Hauts-de-Seine · Yvelines · Val-de-Marne
                        </p>
                        <h1 className="heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mt-5 rise-2 speakable-headline" data-testid="hero-h1" data-speakable="true">
                            Réparateur électroménager{" "}
                            <span className="block text-[var(--amber)]">à domicile, sous 24 h.</span>
                        </h1>
                        <p className="mt-6 text-lg text-[var(--ink-soft)] max-w-2xl rise-3">
                            Lave-linge, frigo, lave-vaisselle, four, sèche-linge — toutes marques.
                            Diagnostic clair, devis gratuit, réparation garantie 18 mois. Réparons plutôt que jeter.
                        </p>

                        <div className="mt-8 flex flex-wrap gap-3 rise-4">
                            <a href={PHONE_TEL} className="btn-amber inline-flex items-center gap-2 px-5 py-3.5 rounded-full text-base" data-testid="hero-cta-phone">
                                <i className="fa-solid fa-phone-volume" /> Appeler {PHONE_DISPLAY}
                            </a>
                            <Link to="/contact" className="btn-outline-navy inline-flex items-center gap-2 px-5 py-3.5 rounded-full text-base font-semibold" data-testid="hero-cta-quote">
                                <i className="fa-regular fa-pen-to-square" /> Devis gratuit en ligne
                            </Link>
                        </div>

                        <ul className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-[var(--ink-soft)]">
                            {[
                                ["fa-shield-halved", "Garantie 18 mois"],
                                ["fa-bolt", "Sous 24–48 h"],
                                ["fa-euro-sign", "Devis transparent"],
                                ["fa-leaf", "Écoresponsable"],
                            ].map(([i, t]) => (
                                <li key={t} className="flex items-center gap-2"><i className={`fa-solid ${i} text-[var(--amber)]`} /> {t}</li>
                            ))}
                        </ul>
                    </div>

                    <aside className="lg:col-span-5 relative">
                        <div className="rounded-3xl overflow-hidden border border-[var(--line)] shadow-sm mb-5 aspect-[4/3]" data-testid="hero-image">
                            <ResponsiveImage
                                slug="hero-laundry"
                                alt="Buanderie moderne avec lave-linge — exemple d'intervention Electrocase à domicile"
                                priority
                                width="800"
                                height="600"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="card-soft p-6 lg:p-7 relative rise-3">
                            <div className="flex items-center gap-3 mb-5">
                                <div className="w-12 h-12 rounded-2xl bg-[var(--navy)] text-[var(--amber)] grid place-items-center"><i className="fa-solid fa-headset text-lg" /></div>
                                <div>
                                    <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)]">Urgence panne</p>
                                    <p className="heading text-xl font-extrabold">Réponse immédiate</p>
                                </div>
                            </div>
                            <ul className="space-y-3 text-sm">
                                {[
                                    "Diagnostic à domicile par technicien certifié",
                                    "Pièces d'origine ou premium équivalentes",
                                    "Devis ferme — pas de frais cachés",
                                    "Habilitation fluides frigorigènes (cat. I)",
                                    "Paiement après réparation",
                                ].map((t) => (
                                    <li key={t} className="flex items-start gap-3">
                                        <i className="fa-solid fa-check text-[var(--amber)] mt-0.5" />
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                            <a href={PHONE_TEL} className="phone-pill mt-6 inline-flex items-center gap-2 w-full justify-center px-4 py-3 rounded-full font-semibold" data-testid="hero-side-phone">
                                <i className="fa-solid fa-phone-volume" /> {PHONE_DISPLAY}
                            </a>
                        </div>
                    </aside>
                </div>
            </section>

            {/* RÉSUMÉ CONCIS + TABLE DES MATIÈRES (GEO-friendly, AI-Ready) */}
            <section
                id="resume"
                aria-labelledby="resume-h2"
                className="max-w-7xl mx-auto px-5 lg:px-10 pt-10 lg:pt-14"
            >
                <div className="grid lg:grid-cols-12 gap-8">
                    {/* TL;DR — résumé concis pour moteurs IA */}
                    <div className="lg:col-span-7 card-soft p-6 lg:p-8 border-l-4 border-[var(--amber)]" role="doc-abstract">
                        <p className="text-xs uppercase tracking-[0.18em] text-[var(--amber)] font-bold mb-3">
                            <i className="fa-solid fa-bolt mr-1.5" />En bref
                        </p>
                        <h2 id="resume-h2" className="heading text-xl lg:text-2xl font-extrabold mb-3">
                            Réparation électroménager à domicile, Paris &amp; Île-de-France
                        </h2>
                        <p className="text-[var(--ink-soft)] leading-relaxed">
                            <strong>Electrocase</strong> répare lave-linge, frigo, lave-vaisselle, four et sèche-linge —
                            toutes marques — directement chez vous à Paris (75), Hauts-de-Seine (92), Yvelines (78) et
                            Val-de-Marne (94). Intervention sous <strong>24 à 48 h</strong>, devis gratuit ferme avant réparation,
                            <strong> garantie 18 mois</strong> pièces et main-d&apos;œuvre — en complément de la {" "}
                            <a href="https://www.service-public.fr/particuliers/vosdroits/F11094" target="_blank" rel="noopener noreferrer external" className="text-[var(--navy)] underline decoration-[var(--amber)] decoration-2 underline-offset-2 font-medium">
                                garantie légale de conformité
                            </a>.
                            Tarifs transparents : <strong>devis gratuit par téléphone</strong>. Techniciens certifiés {" "}
                            <a href="https://www.qualirepar.fr/annuaire" target="_blank" rel="noopener noreferrer external" className="text-[var(--navy)] underline decoration-[var(--amber)] decoration-2 underline-offset-2 font-medium">
                                QualiRépar
                            </a>,
                            habilités {" "}
                            <a href="https://www.ecologie.gouv.fr/indice-reparabilite" target="_blank" rel="noopener noreferrer external" className="text-[var(--navy)] underline decoration-[var(--amber)] decoration-2 underline-offset-2 font-medium">
                                fluides frigorigènes catégorie I
                            </a>.
                        </p>
                    </div>

                    {/* Table des matières */}
                    <TableOfContents items={TOC_ITEMS} />
                </div>

                {/* Featured tools — linkable assets (calculator + study) */}
                <div className="mt-8 lg:mt-10 grid md:grid-cols-2 gap-4">
                    <Link
                        to="/reparer-ou-racheter"
                        className="block group"
                        data-testid="hero-calculator-banner"
                    >
                        <div className="rounded-2xl bg-[var(--navy)] text-[var(--cream)] p-6 lg:p-7 flex items-start gap-4 h-full hover:bg-[var(--navy-deep)] transition-colors">
                            <span className="w-12 h-12 rounded-xl bg-[var(--amber)] grid place-items-center text-[var(--navy)] text-xl shrink-0">
                                <i className="fa-solid fa-calculator" />
                            </span>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs uppercase tracking-[0.18em] text-[var(--amber)] font-bold">Outil gratuit</p>
                                <p className="heading text-base lg:text-lg font-extrabold mt-1">Réparer ou racheter ? Le verdict en 30 s.</p>
                                <p className="text-xs text-[var(--cream)]/70 mt-1">Seuils ADEME + Bonus Réparation</p>
                            </div>
                            <span className="text-[var(--amber)] text-xl group-hover:translate-x-1 transition-transform"><i className="fa-solid fa-arrow-right" /></span>
                        </div>
                    </Link>
                    <Link
                        to="/etudes/pannes-2026"
                        className="block group"
                        data-testid="hero-study-banner"
                    >
                        <div className="rounded-2xl border-2 border-[var(--navy)] bg-white text-[var(--navy)] p-6 lg:p-7 flex items-start gap-4 h-full hover:bg-[var(--paper)] transition-colors">
                            <span className="w-12 h-12 rounded-xl bg-[var(--navy)] grid place-items-center text-[var(--amber)] text-xl shrink-0">
                                <i className="fa-solid fa-chart-column" />
                            </span>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs uppercase tracking-[0.18em] text-[var(--amber)] font-bold">Étude 2026</p>
                                <p className="heading text-base lg:text-lg font-extrabold mt-1">Pannes électroménager à Paris : les données</p>
                                <p className="text-xs text-[var(--ink-soft)] mt-1">1 280 interventions analysées</p>
                            </div>
                            <span className="text-[var(--amber)] text-xl group-hover:translate-x-1 transition-transform"><i className="fa-solid fa-arrow-right" /></span>
                        </div>
                    </Link>
                </div>
            </section>

            {/* STATS */}
            <section className="bg-[var(--navy)] text-[var(--cream)]" aria-label="Chiffres clés">
                <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {STATS.map((s) => (
                        <div key={s.l} className="text-center">
                            <p className="heading text-3xl md:text-4xl font-extrabold text-[var(--amber)]">{s.v}</p>
                            <p className="text-xs md:text-sm uppercase tracking-widest text-[var(--cream)]/75 mt-1">{s.l}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* SERVICES */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20" aria-labelledby="services-h2">
                <div className="max-w-3xl">
                    <div className="divider-amber mb-5" />
                    <h2 id="services-h2" className="heading text-3xl lg:text-4xl font-extrabold">Nos services de réparation électroménager</h2>
                    <p className="mt-4 text-[var(--ink-soft)] text-lg">
                        Une équipe spécialisée par type d&apos;appareil. Toutes marques, toutes pannes, à domicile.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
                    {SERVICES.map((s) => (
                        <Link key={s.slug} to={`/${s.slug}`} className="card-soft block group overflow-hidden" data-testid={`service-card-${s.slug}`}>
                            <div className="aspect-[16/9] overflow-hidden bg-[var(--paper)]">
                                <ResponsiveImage
                                    slug={SERVICE_IMG[s.slug] || "technician-tools"}
                                    alt={`${s.nav} — réparation à domicile par technicien Electrocase`}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                            </div>
                            <div className="p-6">
                                <div className="flex items-center justify-between">
                                    <div className="w-12 h-12 rounded-2xl bg-[var(--paper)] grid place-items-center text-[var(--navy)] group-hover:bg-[var(--amber)] transition-colors">
                                        <i className={`${s.icon} text-lg`} />
                                    </div>
                                    <i className="fa-solid fa-arrow-right text-[var(--ink-soft)] group-hover:text-[var(--amber)] group-hover:translate-x-1 transition" />
                                </div>
                                <h3 className="heading text-xl font-bold mt-5">{serviceTitle(s.nav)}</h3>
                                <p className="mt-2 text-sm text-[var(--ink-soft)] line-clamp-3">{s.intro}</p>
                                <span className="mt-4 inline-block text-sm font-semibold text-[var(--navy)] link-underline">En savoir plus</span>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* WHY US */}
            <section className="bg-[var(--paper)] border-y border-[var(--line)]" aria-labelledby="why-h2">
                <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
                    <div className="grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-4">
                            <div className="divider-amber mb-5" />
                            <h2 id="why-h2" className="heading text-3xl lg:text-4xl font-extrabold">Pourquoi choisir Electrocase ?</h2>
                            <p className="mt-4 text-[var(--ink-soft)]">
                                Un service local, humain et fiable. Des techniciens qui aiment leur métier et qui prennent le temps d&apos;expliquer. Nous appliquons les bonnes pratiques de l&apos;{" "}
                                <a href="https://www.ademe.fr/particuliers-eco-citoyens/maison/electromenager-multimedia/" target="_blank" rel="noopener noreferrer external" className="text-[var(--navy)] underline decoration-[var(--amber)] decoration-2 underline-offset-2 font-medium">
                                    ADEME pour l&apos;électroménager
                                </a>
                                {" "}et facilitons l&apos;accès au{" "}
                                <a href="https://www.ecosystem.eco/fr/article/bonus-reparation" target="_blank" rel="noopener noreferrer external" className="text-[var(--navy)] underline decoration-[var(--amber)] decoration-2 underline-offset-2 font-medium">
                                    Bonus Réparation Ecosystem
                                </a>
                                {" "}lorsque votre appareil est éligible.
                            </p>
                        </div>
                        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
                            {WHY.map((w) => (
                                <article key={w.t} className="card-soft p-6">
                                    <div className="w-11 h-11 rounded-xl bg-[var(--navy)] text-[var(--amber)] grid place-items-center mb-4">
                                        <i className={`${w.i} text-lg`} />
                                    </div>
                                    <h3 className="heading text-lg font-bold">{w.t}</h3>
                                    <p className="mt-2 text-sm text-[var(--ink-soft)]">{w.d}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ZONES */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20" aria-labelledby="zones-h2">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                    <div className="max-w-2xl">
                        <div className="divider-amber mb-5" />
                        <h2 id="zones-h2" className="heading text-3xl lg:text-4xl font-extrabold">Nos zones d&apos;intervention en Île-de-France</h2>
                        <p className="mt-4 text-[var(--ink-soft)] text-lg">
                            Paris, Hauts-de-Seine, Yvelines et Val-de-Marne. Service rapide, technicien proche de chez vous.
                        </p>
                    </div>
                    <Link to="/zones-intervention" className="btn-outline-navy inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold w-fit" data-testid="zones-cta">
                        Voir toutes les villes <i className="fa-solid fa-arrow-right" />
                    </Link>
                </div>
                <div className="rounded-3xl overflow-hidden mt-10 aspect-[21/9]" data-testid="zones-image">
                    <ResponsiveImage
                        slug="paris-rooftops"
                        alt="Toits de Paris et Île-de-France — zone d'intervention Electrocase"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="grid md:grid-cols-2 gap-5 mt-10">
                    {Object.entries(ZONES).map(([dept, cities]) => (
                        <div key={dept} className="card-soft p-6">
                            <h3 className="heading text-lg font-bold flex items-center gap-2">
                                <i className="fa-solid fa-location-dot text-[var(--amber)]" /> {dept}
                            </h3>
                            <p className="mt-3 text-sm text-[var(--ink-soft)] leading-relaxed">
                                {cities.slice(0, 10).join(" · ")}{cities.length > 10 ? "…" : ""}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* TOP REVIEWS (les meilleurs avis clients) */}
            <ReviewsSection />

            {/* VOICE-FRIENDLY FAQ (Speakable / Google Assistant / Alexa / Siri) */}
            <VoiceFaqSection items={VOICE_FAQ} />

            {/* FAQ */}
            <MainFaqSection items={FAQ} />

            {/* LIENS DÉCOUVERTE — maillage interne in-content */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 pt-4 pb-2" data-testid="home-discover-links">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        { to: "/carte-interventions-idf", icon: "fa-map-location-dot", label: "Carte des interventions", desc: "5 120 dépannages en Île-de-France sur 94 villes" },
                        { to: "/notre-methodologie", icon: "fa-clipboard-check", label: "Notre méthodologie", desc: "Diagnostic, devis et réparation : comment on travaille" },
                        { to: "/a-propos", icon: "fa-people-group", label: "À propos d'Electrocase", desc: "L'équipe et les valeurs derrière vos réparations" },
                        { to: "/plan-du-site", icon: "fa-sitemap", label: "Plan du site", desc: "Les 737 pages : villes, pannes, codes erreur, marques" },
                    ].map((l) => (
                        <Link key={l.to} to={l.to} data-testid={`home-discover-${l.to.slice(1)}`} className="group border border-[var(--line)] rounded-2xl p-5 bg-white hover:border-[var(--amber)] hover:shadow-md transition">
                            <i className={`fa-solid ${l.icon} text-xl text-[var(--amber)]`} />
                            <p className="font-black text-[var(--navy)] mt-2 group-hover:text-[var(--amber)] transition">{l.label}</p>
                            <p className="text-sm text-[var(--ink-soft)] mt-1">{l.desc}</p>
                        </Link>
                    ))}
                </div>
            </section>

            {/* CTA BAND */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
                <div className="rounded-3xl bg-[var(--navy)] text-[var(--cream)] p-8 lg:p-14 grid lg:grid-cols-2 gap-10 items-center relative overflow-hidden grain">
                    <div className="relative">
                        <p className="text-xs uppercase tracking-[0.2em] text-[var(--amber)] font-semibold">Besoin d&apos;un dépannage ?</p>
                        <h2 className="heading text-3xl lg:text-4xl font-extrabold text-[var(--cream)] mt-3">
                            Un technicien chez vous, dès aujourd&apos;hui.
                        </h2>
                        <p className="mt-4 text-[var(--cream)]/80 max-w-lg">
                            Appelez-nous : on diagnostique, on chiffre, on répare. Pas de surprise, pas de blabla.
                        </p>
                    </div>
                    <div className="relative flex flex-col sm:flex-row gap-3 lg:justify-end">
                        <a href={PHONE_TEL} className="btn-amber inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-bold" data-testid="cta-band-phone">
                            <i className="fa-solid fa-phone-volume" /> {PHONE_DISPLAY}
                        </a>
                        <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-bold border border-[var(--amber)]/40 text-[var(--amber)] hover:bg-[var(--amber)] hover:text-[var(--navy)] transition" data-testid="cta-band-form">
                            <i className="fa-regular fa-pen-to-square" /> Demander un devis
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
